import { PowerShellCommand } from '../data/powershell';
import { LogLevel } from '../diagnostics/log-level';
import { LogRecord } from '../diagnostics/log-record';
import { Logging } from '../diagnostics/logging';
import { Strings } from '../generated/strings';

/**
 * Defines an object or property for other extensions to reference
 */
export interface EnvironmentModulePropertyDescriptor {
    /**
     * The type of value of this property.
     * required if schema is not defined.
     */
    type?: 'array' | 'boolean' | 'null' | 'object' | 'number' | 'string';

    /**
     * A reference to a schema resource defining this property
     */
    schema?: string;

    /**
     * Tells other extension developers more information about this property
     */
    comment?: string;

    /**
     * Indicates if this field is required
     */
    required?: boolean;

    /**
     * The default value of this property
     * The type should be of the same type defined in the 'type' property.
     */
    defaultsTo?: any;

    /**
     * The contract for the array items.
     * Required if the type property is set to 'array'
     */
    items?: EnvironmentModulePropertyDescriptor;

    /**
     * The contract for the sub properties.
     * Required if the type property is set to 'object'
     */
    properties?: MsftSme.StringMap<EnvironmentModulePropertyDescriptor>;
}

export interface EnvironmentModuleDisplayable {
    /**
     * display name of module
     */
    displayName: string;

    /**
     * description of module
     */
    description: string;

    /**
     * icon url or smeIcon reference. Absolute or relative urls
     */
    icon: string;

    /**
     * The scale of icon. (1.0 is default if not specified)
     */
    iconScale?: number;

    /**
     * The dark theme data. (Optional)
     */
    darkTheme?: {
        /**
         * The icon path on the package.
         */
        icon: string;

        /**
         * The scale of icon. (1.0 is default if not specified)
         */
        iconScale?: number;
    };

    /**
     * keywords for module search
     */
    keywords: string[];
}

/**
 * The type of entry point.
 */
export enum EnvironmentModuleEntryPointType {
    ConnectionProvider = 'connectionProvider',
    Solution = 'solution',
    Tool = 'tool',
    Service = 'service',
    SettingsForm = 'settingsForm',
    GroupProvider = 'groupProvider',
    Worker = 'worker',
    Dialog = 'dialog',
    SnapIn = 'snapIn'
}

/**
 * The type of behavior to solution root navigation.
 */
export type SolutionRootNavigationBehaviorType = 'path' | 'connections' | 'tools';

/**
 * The interface of condition check of server inventory data.
 */
export interface EnvironmentModuleConditionStatement {
    /**
     * The operator of the condition.
     */
    operator: 'gt' | 'ge' | 'lt' | 'le' | 'eq' | 'ne' | 'is' | 'not' | 'contains' | 'notContains'
    | 'anyEq' | 'anyNe' | 'anyContains' | 'anyNotContains';

    /**
     * The data type of value.
     */
    type: 'version' | 'number' | 'string' | 'boolean' | 'numberArray' | 'stringArray';

    /**
     * The test value of the condition.
     */
    value: string | number | boolean | number[] | string[];
}

/**
 * The mapped inventory conditions.
 */
export type EnvironmentModuleConditionMap = MsftSme.StringMap<EnvironmentModuleConditionStatement>;

/**
 * The requirement of entry point to be activated on the view.
 */
export interface EnvironmentModuleEntryPointRequirement {
    /**
     * The id of solutions of the connection type that satisfies this requirement.
     */
    solutionIds?: string[];

    /**
     * The id of the connection type that satisfies this requirement
     */
    connectionTypes?: string[];

    /**
     * The conditions of tool status.
     */
    conditions?: {
        /**
         * Check if app/desktop localhost. Only tested if it sets "false".
         */
        localhost?: boolean,

        /**
         * Check if specific condition against ServerInventory data.
         */
        inventory?: EnvironmentModuleConditionMap,

        /**
         * Check if specific condition against supported installation type data.
         */
        installationTypes?: string[],

        /**
         * Check if specific condition against Connection properties data.
         */
        properties?: EnvironmentModuleConditionMap,

        /**
         * @deprecated
         * Check by using PowerShell script.
         *
         * must return the following format.
         * {
         *   state: 'Available', 'NotConfigured', 'NotSupported'
         *   message: string
         *   keyValuePairs: [ { name: string, value: string, type: string } ]
         * }
         */
        script?: string

        /**
         * Check by using PowerShell script and command.
         *
         * must return the following format.
         * {
         *   state: 'Available', 'NotConfigured', 'NotSupported'
         *   message: string
         *   keyValuePairs: [ { name: string, value: string, type: string } ]
         * }
         */
        powerShell?: PowerShellCommand;
    }[];
}

/**
 * Defines an object that describes the identity of an extension method
 */
export interface ExtensionMethodIdentifier {
    /**
     * The name of the module in which the extension method can be found
     */
    module: string;
    /**
     * The name of the entry point in which the extension method can be found
     */
    name: string;
    /**
     * The method to find
     */
    method: string;
    /**
     * The version of the method to be found
     */
    version: number;
}

/**
 * Defines an object that describes the identity of an extension dialog
 */
export interface ExtensionDialogIdentifier {
    /**
     * The name of the module in which the extension dialog can be found
     */
    module: string;
    /**
     * The name of the entry point of the extension dialog
     */
    name: string;
    /**
     * The version of the dialog
     */
    version: number;
}

export interface EnvironmentModuleConnectionStatusProvider {
    /**
     * If defined, powershell will be used to query a connections status
     */
    powerShell?: PowerShellCommand;

    /**
     * If defined, the gateways REST api will be used to query a connections status
     */
    relativeGatewayUrl?: string;

    /**
     * Specifies to get the connection status from a worker extension method.
     * The worker method is expected to take a connection name as its only input
     */
    worker?: ExtensionMethodIdentifier;

    /**
     * Specifies to get the connection status from a service extension method.
     * The service method is expected to take a connection name as its only input
     */
    service?: ExtensionMethodIdentifier;

    /**
     * If defined, will be used to look up display strings for the label and description of the status object
     */
    displayValueMap?: MsftSme.StringMap<string>;

    /**
     * If defined, will be used to skip status checks
     */
    skipStatusCheck?: boolean;
}

export interface SolutionConnectionsViewConfiguration {
    /**
     * List of connections types to show in the view of the connections list
     * each entry should be a valid connection type string identifier from any
     * modules entrypoints of type @see EnvironmentModuleEntryPointType.ConnectionProvider
     */
    connectionTypes: string[];

    /**
     * The title of the connections list for this solution.
     * Optional. A default header will be provided if this field is left out
     */
    header?: string;
}

export interface SolutionToolsViewConfiguration {
    /**
     * Enables/Disable ths tools menu
     */
    enabled: boolean;

    /**
     * Sets the title of the tools menu
     */
    title: string;

    /**
     * Sets the title of the search field on the tools menu
     */
    searchTitle: string;

    /**
     * Indicates the default tool to open if no tool is provided. This value should be the name of an entrypoint in the same module
     */
    defaultTool: string;

    /**
     * The list of known tools that will be displayed at the first section of tool menu.
     * 1) entry and group are mandatory.
     * 2) create groups and sort members by priority then alphabetical.
     * 3) sort the groups by highest priority number in the group members.
     * 4) settings creates single group and sort members by priority then alphabetical.
     */
    predefined: {
        /**
         * The entry point ID with "<module name>!<entry point name>" format.
         */
        id: string;

        /**
         * The group display name.
         */
        group: string;

        /**
         * The ordering priority.
         * (0 is highest)
         */
        priority?: number;

        /**
         * Indicating the tool is settings menu.
         */
        settings?: boolean;
    }[];
}

/**
 * Defines the configuration for a connection provider experience
 */
export interface ConnectionProviderExperienceConfiguration {
    /**
     * The path to the experience.
     */
    path: string;

    /**
     * The label to display for the experience.
     * ex) Add Server Connection, Create Failover Cluster Connection and so on.
     */
    label: string;

    /**
     * The dialog mode to use for the dialog that contains this experience
     */
    dialogMode?: 'normal' | 'wide' | 'compact' | 'fullscreen';
}

/**
 * The type of lifetime to implement when creating a worker
 */
export type WorkerLifetime = 'default' | 'long';

/**
 * The type of startup type to implement when creating a service
 */
export type ServiceStartupType = 'automatic' | 'onDemand';

/**
 * The type of cleanup type to implement when creating a service
 * 'never' means that that service will start and only be destroyed if specifically requested or the application ends
 * 'onIdle' means that the shell will clean up the service when no one has used it within a certain time limit.
 * This time limit is currently controlled by the shell.
 */
export type ServiceCleanupType = 'never' | 'onIdle';
/**
 * Defines the target of an entry point
 */
export interface ExtenderDefinition {
    /**
     * The module to target
     */
    module: string;
    /**
     * The name of the extension target (within the 'module') to provide this entry point to
     */
    target: string;
}

/**
 * Defines the target of a worker entry point
 */
export interface WorkerExtenderDefinition extends ExtenderDefinition {
    /**
     * A map of method names to the versions that are implemented in this worker
     */
    methodVersions: MsftSme.StringMap<number>;
}

/**
 * Describes the method contract exposed by a worker for other modules to call.
 * When making breaking changes, make sure to use the version properties so dependent modules still work with an older contract
 */
export interface ExtensionMethodContract {
    /**
     * The version of this method. Create a new version for every breaking change
     */
    version?: number;
    /**
     * The name of the method
     */
    method: string;
    /**
     * a comment describing this method to consumers
     */
    comment?: string;
    /**
     * Defines the arguments expected to be passed to this method
     */
    arguments?: MsftSme.StringMap<EnvironmentModulePropertyDescriptor>;
    /**
     * Defines the contract that this method will return
     */
    returns: EnvironmentModulePropertyDescriptor;
}

/**
 * Describes the declaration for a dialog
 * When making breaking changes, make sure to use the version properties so dependent modules still work with an older contract
 */
export interface DialogExtensionDeclaration {
    /**
     * The version of this method. Create a new version for every breaking change
     */
    version?: number;
    /**
     * a comment describing this dialog to consumers
     */
    comment?: string;
    /**
    * The dialog mode to use for the dialog
    */
    dialogMode?: 'pane' | 'wide-pane' | 'compact' | 'compact-square' | 'fullscreen' | 'centered' | 'centered-large';
    /**
     * Defines the arguments expected to be passed to this method
     */
    arguments?: MsftSme.StringMap<EnvironmentModulePropertyDescriptor>;
    /**
     * Defines the contract that this dialog will return
     */
    returns: EnvironmentModulePropertyDescriptor;
}

/**
 * Define the minimum and maximum footprint of a snap in
 */
export interface SnapInSizeConstraints {
    minWidth: number;
    minHeight: number;
    maxWidth: number;
    maxHeight: number;
}

/**
 * Define the component interaction of inputs and outputs between the entry point and its consumer
 */
export interface ExtensionInteraction {
    /**
    * The version of this interaction. Create a new version for every breaking change
    */
    version?: number;
    /**
     * a comment describing this interaction model
     */
    comment?: string;
    /**
     * Defines the inputs that can be passed to this entry point
     */
    inputs?: MsftSme.StringMap<EnvironmentModulePropertyDescriptor>;
    /**
     * Defines the outputs that this entry point can return
     */
    outputs?: MsftSme.StringMap<EnvironmentModulePropertyDescriptor>;
}

/**
 * The type of lifetime to implement when creating a worker
 */
export enum ExtensionTargetType {
    WorkerTarget = 'workerTarget'
}

export interface ExtensionTarget {
    /**
     * The type of extension target
     */
    extensionTargetType: ExtensionTargetType;

    /**
     * Description of the workerTarget.
     * This should tell the consumer what workers who target this are expected to do.
     */
    description: string;

    /**
     * The name of this extension target.
     * Should be unique to this modules other extension targets
     */
    name: string;

    /**
     * Mapping to the parent module of this target
     */
    parentModule?: EnvironmentModule;
}

export interface WorkerExtensionTarget extends ExtensionTarget {

    /**
     * The type of extension target
     */
    extensionTargetType: ExtensionTargetType.WorkerTarget;

    /**
     * worker only -- The contract that is expected to be fulfilled by worker entry points that extend this target
     */
    contract: ExtensionMethodContract[];
}

/**
 * Defines the provider for collecting active managing as information
 */
export interface ManagingAsProvider {
    /**
     * If Defined, Specifies to collect managing as data from a worker method
     */
    worker?: ExtensionMethodIdentifier;
    /**
     * If Defined, Specifies to collect managing as data from a service method
     */
    service?: ExtensionMethodIdentifier;
}

/**
 * Defines how to apply manage as scenarios to this connection type
 */
export interface EnvironmentModuleManageAsScheme {
    /**
     * If Defined, Specifies to configure manage as credentials from an extension dialog
     */
    configurationDialog: ExtensionDialogIdentifier;
    /**
     * If Defined, Specifies how to collect active managing as data
     */
    managingAsProvider: ManagingAsProvider;
}

/**
 * Defines a modules entry point
 */
export interface EnvironmentModuleEntryPoint extends EnvironmentModuleDisplayable {
    /**
     * The type of entry point.
     */
    entryPointType: EnvironmentModuleEntryPointType;

    /**
     * The name of entry point, unique to its parent module
     */
    name?: string;

    /**
     * The friendly url name to use to reference this entrypoint. This must be unique among all entrypoints
     */
    urlName?: string;

    /**
     * The path of entry point.
     */
    path: string;

    /**
     * The frame Id to share single iframe within the module.
     */
    iframeId?: string;

    /**
     * Experiment keys that must all be enabled to enable this entry point
     */
    experimentKeys?: string[];

    /**
     * The id of the group provider.
     * Used to indicate which group provider should be used for grouping extensions
     */
    groupId?: string;

    /**
     * @see EnvironmentModuleEntryPointType.ConnectionProvider types only.
     * NOTE: This SHOULD be for SOLUTION types. since a connection type can exist in multiple solutions.
     * If true, the search bar will shown the tools list for this connection type
     * Otherwise, search bar will be hidden. Default is true.
     */
    searchBar?: boolean;

    /**
     * @see EnvironmentModuleEntryPointType.ConnectionProvider types only.
     * Alternative to providing "path".
     * Configures the experiences for manipulating the connections provided by this entry point
     */
    experiences: {
        add: ConnectionProviderExperienceConfiguration;
        create: ConnectionProviderExperienceConfiguration;
    };

    /**
     * @see EnvironmentModuleEntryPointType.ConnectionProvider types only.
     * The id of the connection type that this entry point provides
     */
    connectionType?: string;

    /**
     * @see EnvironmentModuleEntryPointType.ConnectionProvider types only.
     * The friendly url name to use to reference the connection type that this entry point provides.
     * This must be unique among all connection types
     */
    connectionTypeUrlName?: string;

    /**
     * @see EnvironmentModuleEntryPointType.ConnectionProvider types only.
     * The display name of the connection type that this entry point provides
     */
    connectionTypeName?: string;

    /**
     * @see EnvironmentModuleEntryPointType.ConnectionProvider types only.
     * The default solution to use when opening the connection type that this entry point provides
     */
    connectionTypeDefaultSolution?: string;

    /**
     * @see EnvironmentModuleEntryPointType.ConnectionProvider types only.
     * The status provider implementation for the connection type that this entry point provides
     */
    connectionStatusProvider?: EnvironmentModuleConnectionStatusProvider;

    /**
     * @see EnvironmentModuleEntryPointType.ConnectionProvider types only.
     * The manage as scheme to use for changing how the connection is managed
     */
    manageAsScheme?: EnvironmentModuleManageAsScheme;

    /**
     * @see EnvironmentModuleEntryPointType.Tool types only.
     * A list of requirements, if any requirement is met then this tool is applicable to a given connection
     */
    requirements?: EnvironmentModuleEntryPointRequirement[];

    /**
     * Mapping to the parent module of this entry point
     */
    parentModule?: EnvironmentModule;

    /**
     * @see EnvironmentModuleEntryPointType.Solution types only.
     * Defines how navigating to the root of this solution will behave.
     * Setting this to 'path' will load this module to the 'path' property.
     * Setting this to 'connections' will navigate instead to the connections list, customized for this solution
     */
    rootNavigationBehavior?: SolutionRootNavigationBehaviorType;

    /**
     * @see EnvironmentModuleEntryPointType.Solution types only.
     * Only applicable when 'rootNavigationBehavior' us set to 'connections'.
     * Describes how to customize the view of the connections list.
     */
    connections?: SolutionConnectionsViewConfiguration;

    /**
     * @see EnvironmentModuleEntryPointType.Solution types only. If provided, Configures the tools menu for this solution.
     */
    tools?: SolutionToolsViewConfiguration;

    /**
     * @see EnvironmentModuleEntryPointType.Worker types only. If provided, Configures the lifetime of this worker.
     */
    lifetime?: WorkerLifetime;

    /**
     * @see EnvironmentModuleEntryPointType.Service types only. If provided, Configures the startup type of this service.
     */
    startupType?: ServiceStartupType;

    /**
     * @see EnvironmentModuleEntryPointType.Service types only. If provided, Configures the cleanup of this service.
     */
    cleanupType?: ServiceCleanupType;

    /**
     * Defines specific extensionTargets to provide this entry point to
     */
    extends?: ExtenderDefinition[];

    /**
     * @see EnvironmentModuleEntryPointType.Worker and @see EnvironmentModuleEntryPointType.Service types only.
     * Describes the method contracts exposed by this worker or service for other modules to call.
     */
    exposes?: ExtensionMethodContract[];

    /**
     * @see EnvironmentModuleEntryPointType.Dialog types only
     * Defines the declaration for this dialog - inputs and outputs.
     */
    declaration?: DialogExtensionDeclaration[];

    /**
     * @see EnvironmentModuleEntryPointType.SnapIn types only
     * Defines the size requirements for this snap in
     */
    sizeConstrains?: SnapInSizeConstraints;

    /**
     * @see EnvironmentModuleEntryPointType.SnapIn types only
     * Defines the component interaction model for the snap in
     */
    interaction?: ExtensionInteraction;

    /**
     * @see EnvironmentModuleEntryPointType.SnapIn types only
     * Defines the author of the snap in.
     */
    author?: string;
}

/**
 * The state definition of Tool condition.
 */
export enum EnvironmentModuleToolState {
    /**
     * Tool is available on the connection.
     */
    Available,

    /**
     * Tool is not available because it's not configured properly.
     */
    NotConfigured,

    /**
     * Tool it not supported on the connection.
     */
    NotSupported
}

/**
 * The result of Tool conditions.
 */
export interface EnvironmentModuleToolConditionResult {
    /**
     * Display the tool.
     */
    show: boolean;

    /**
     * The connection id of node.
     */
    connectionId?: string;

    /**
     * The state of tool condition.
     */
    detail?: EnvironmentModuleToolState;

    /**
     * The message of condition.
     */
    message?: string;
}

/**
 * Tool's combined entry point and inventory result.
 */
export interface EnvironmentModuleEntryPointWithToolConditionResult
    extends EnvironmentModuleEntryPoint, EnvironmentModuleToolConditionResult {
}

/**
 * Environment module resource class.
 */
export interface EnvironmentModuleResource {
    /**
     * locale key string.
     */
    locale: string;

    /**
     * string map object.
     */
    strings: MsftSme.StringMap<string>;
}

export const defaultEnvironmentConfiguration: MsftSme.EnvironmentConfiguration = {
    gateway: {
        disabled: false
    },
    appBar: {
        title: {
            hide: false
        },
        solution: {
            hide: false,
            hideDropdown: false
        },
        navigation: {
            allowHistory: false,
            allowRefresh: false,
            allowUrlInput: false,
            allowHome: false
        },
        urlField: {
            hideSolutionUrl: true
        },
        rightMenu: {
            showScripts: true,
            showSettings: true,
            showAbout: true,
            showWindowControls: false,
            showShareLink: false
        },
        aboutDialog: {
            showFeedback: true,
            feedbackLink: 'https://go.microsoft.com/fwlink/?linkid=2033442',
            showDocumentation: true,
            documentationLink: 'https://go.microsoft.com/fwlink/?linkid=2150525',
            releaseNotesLink: 'https://aka.ms/honolulucurrent',
            seeMoreReleaseDetailsLink: 'https://go.microsoft.com/fwlink/?linkid=2152971',
            showLicense: true,
            showScriptsUseRights: true,
            showLicenseExtensions: true,
            showPrivacyStatement: true,
            privacyLink: 'https://aka.ms/winserverprivacy',
            showSupportPolicy: true,
            supportLink: 'https://aka.ms/wac-support-policy',
            showThirdPartyNotice: true,
            showShortcuts: true,
            openLinkWithoutHref: false,
            showAccessibilityCompliance: false,
            accessibilityComplianceLink: 'https://go.microsoft.com/fwlink/?linkid=2121429'
        },
        overview: {
            gatewayStatusUpdateLink: 'settings/updates',
            updateLearnMoreLink: 'https://aka.ms/honolulucurrent'
        }
    },
    extensionsAutoUpdate: {
        enable : true,
        showAlert: true
    },
    gatewayAutoUpdate: {
        enable: true
    },
    whatsNew: {
        showDialog: true
    },
    tools: {
        hide: false
    },
    connections: {
        type: 'standard',
        header: {
            hide: false
        }
    },
    telemetry: {
        sourceLibraryCdnLink: 'https://js.monitor.azure.com/scripts/c/ms.analytics-web-3.0.2.min.js',
        // Integrity hash generated via srihash.org
        sourceLibraryCdnIntegrityHash: 'sha384-ZCsysyTvHb4xqC4BgJSay4nwlNIGlT1w5t5YQS5htghoD2XEL2b4SrkhRPhtr9GD'
    },
    settings: {
        account: {
            show: true
        },
        personalization: {
            show: true
        },
        diagnostic: {
            show: true
        },
        language: {
            show: true
        },
        notification: {
            show: true
        },
        extension: {
            show: true
        },
        azure: {
            show: true
        },
        access: {
            show: true
        },
        sharedConnections: {
            show: true
        },
        general: {
            show: true
        },
        connectivity: {
            show: true
        },
        updates: {
            show: true
        },
        extensionUpdates: {
            show: true
        },
        performance: {
            show: true
        },
        advanced: {
            show: true
        },
        proxy: {
            show: true
        }
    }
};

/**
 * Defines an object that provides information about a connection type.
 */
export interface EnvironmentConnectionTypeInfo {
    /**
     * The provider entry point.
     */
    provider: EnvironmentModuleEntryPoint;

    /**
     * The solution entry point.
     */
    solution: EnvironmentModuleEntryPoint;
}

export interface EnvironmentModuleFriendlyUrlMap {
    to: MsftSme.StringMap<string>;
    from: MsftSme.StringMap<string>;
}

export interface EnvironmentModuleFriendlyUrlMaps {
    connectionTypes: EnvironmentModuleFriendlyUrlMap;
    solutions: EnvironmentModuleFriendlyUrlMap;
    tools: EnvironmentModuleFriendlyUrlMap;
    settingsForms: EnvironmentModuleFriendlyUrlMap;
}

/**
 * Environment module class.
 */
export class EnvironmentModule {
    private static readonly logSourceName = 'ManifestLoader';

    /**
     * Static mapping for connection information.
     */
    private static connectionMap: { [name: string]: EnvironmentConnectionTypeInfo } = null;

    /**
     * Static mapping for extensionTargetTypes to a map of extensionTargetIds to entry point ids that fulfill that target
     */
    private static extensionTargetFulfillmentMap: MsftSme.StringMap<string[]>;

    /**
     * Static mapping for friendly url strings to entrypoint ids
     */
    private static friendlyUrlMap: EnvironmentModuleFriendlyUrlMaps = null;

    /**
     * The name of shell.
     */
    public static nameOfShell = 'msft.sme.shell';

    /**
     * The default signature if missing manifest.
     */
    public static defaultSignature = 'version 0.0.0';

    /**
     * The prefix for identifying an sme-icon reference
     */
    public static smeIconPrefix = 'sme-icon:';

    /**
     * All entry points from all modules
     */
    public static allEntryPoints: EnvironmentModuleEntryPoint[] = null;

    /**
     * All entry points by type
     */
    public static entryPointsByType: MsftSme.StringMap<EnvironmentModuleEntryPoint[]> = null;

    /**
     * All entry points by type
     */
    public static entryPointsById: MsftSme.StringMap<EnvironmentModuleEntryPoint> = null;

    /**
     * All extension targets from all modules
     */
    public static allExtensionTargets: ExtensionTarget[] = null;

    /**
     * All extension targets by type
     */
    public static extensionTargetsByType: MsftSme.StringMap<ExtensionTarget[]> = null;

    /**
     * All extension targets by type
     */
    public static extensionTargetsById: MsftSme.StringMap<ExtensionTarget> = null;

    /**
     * All extension targets by type
     */
    public static extensionOrigins: Set<String> = null;

    /**
     * schema file information.
     */
    public $schema?: string;

    /**
     * name of module.
     */
    public name: string;

    /**
     * display name of module.
     */
    public displayName: string;

    /**
     * description of set of tools.
     */
    public description?: string;

    /**
     * keywords of the module.
     */
    public keywords?: string[];

    /**
     * origin absolute url.
     */
    public origin: string;

    /**
     * target url to open the iframe.
     */
    public target?: string;

    /**
     * signature.
     */
    public signature: string;

    /**
     * The version of this module
     */
    public version: string;

    /**
     * icon url, absolute or relative to Shell url.
     */
    public icon?: string;

    /**
     * The support JEA endpoint with PowerShell module.
     */
    public jea?: boolean;

    /**
     * extension targets for this extension
     * Defines things that can be extended in this module.
     */
    public extensionTargets?: ExtensionTarget[];

    /**
     * entry points to launch the tool.
     */
    public entryPoints: EnvironmentModuleEntryPoint[];

    /**
     * Assets shared by the module
     */
    public assets?: MsftSme.MsftSmeModuleAssets;

    /**
     * localized resources of strings.
     */
    public resources: EnvironmentModuleResource[];

    /**
     * Schemas provided for use in this manifest
     */
    public schemas?: MsftSme.StringMap<EnvironmentModulePropertyDescriptor>;

    /**
     * list of all other available modules for shell.
     */
    public modules?: EnvironmentModule[];

    /**
     * Indicates that this module is being sideLoaded
     */
    public isSideLoaded?: boolean;

    /**
     * The Environment configuration
     */
    public configuration: MsftSme.EnvironmentConfiguration;

    /**
     * returns true if the provided origin is a known extension origin
     */
    public static isExtensionOrigin(origin: string): boolean {
        return this.extensionOrigins.has(origin);
    }

    /**
     * Indicates if a given icon reference is an sme-icon or some other form
     * @param iconRef the reference to check
     */
    public static isSmeIconRef(iconRef: string): boolean {
        return (!MsftSme.isNullOrUndefined(iconRef) && iconRef.startsWith(EnvironmentModule.smeIconPrefix));
    }

    /**
     * Gets the icon class from an iconRef or returns null if the iconRef is not an sme-icon reference
     * @param iconRef the reference to check
     */
    public static getSmeIconClassFromRef(iconRef: string): string {
        return (!MsftSme.isNullOrUndefined(iconRef) && EnvironmentModule.isSmeIconRef(iconRef))
            ? iconRef.substring(EnvironmentModule.smeIconPrefix.length) : null;
    }

    /**
     * Gets the icon url from an iconRef or returns null if the iconRef is an sme-icon reference
     * @param iconRef the reference to check
     */
    public static getFormattedIconRefUrl(iconRef: string): string {
        return (MsftSme.isNullOrUndefined(iconRef) || EnvironmentModule.isSmeIconRef(iconRef)) ? null : `url(${iconRef})`;
    }

    /**
     * Processes an icon reference to resolve it to either a sme-icon or absolute url
     * @param iconRef The icon reference
     * @param module the module that the icon belongs to
     */
    public static processIconReference(iconRef: string, module: EnvironmentModule): string {
        // leave icon ref alone if it is an sme-icon, absolute uri, or null/whitespace
        if (MsftSme.isNullOrWhiteSpace(iconRef) || EnvironmentModule.isSmeIconRef(iconRef) || MsftSme.isUriAbsolute(iconRef)) {
            return iconRef;
        }

        return this.toAbsolutePath(module, iconRef);
    }

    /**
     * Converts relative module paths into absolute uris
     */
    private static toAbsolutePath(module: EnvironmentModule, relativePath: string): string {
        const pathComponents = [relativePath];

        if (module.target && !module.isSideLoaded) {
            pathComponents.unshift(module.target);
        }

        pathComponents.unshift(module.origin);
        return MsftSme.pathJoin('/', pathComponents);
    }

    /**
     * Find resource string for the key.
     *
     * @param resources The resource.
     * @param locale The locale.
     * @param key The key string.
     */
    public static findResource(resources: EnvironmentModuleResource[], locale: string, key: string): string {
        const prefix = 'resources:strings:';
        if (!key.startsWith(prefix)) {
            return key;
        }

        key = key.substring(prefix.length);
        const en: EnvironmentModuleResource = resources.first((value: EnvironmentModuleResource) => value.locale === 'default');
        const current: EnvironmentModuleResource = resources.first((value: EnvironmentModuleResource) => value.locale === locale);
        if (current && current.strings[key]) {
            return current.strings[key];
        } else if (en && en.strings[key]) {
            return en.strings[key];
        }

        return key;
    }

    /**
     * Recursively processes manifest resources
     *
     * @param module the manifest object.
     * @param locale the locale string such as 'en'.
     * @param obj current object, defaults to the module itself
     */
    private static processModuleResources(module: EnvironmentModule, locale: string, obj: any = module): void {
        if (!Array.isArray(module.resources)) {
            module.resources = [];
        }

        const keys = Object.keys(obj);
        keys.forEach(key => {
            if (typeof obj[key] === 'string') {
                // first check the string for localized values
                obj[key] = EnvironmentModule.findResource(module.resources, locale, obj[key]);

                // if the key is 'icon' treat this field as an icon
                if (key === 'icon') {
                    obj[key] = EnvironmentModule.processIconReference(obj[key], module);
                }
            } else if (typeof obj[key] === 'object') {
                EnvironmentModule.processModuleResources(module, locale, obj[key]);
            }
        });
    }

    public static getModuleAssetUri(moduleName: string, resourceName: string, version: string): string {
        const assets = MsftSme.self().Resources.moduleAssets;
        if (assets[moduleName] && assets[moduleName][resourceName] && assets[moduleName][resourceName][version]) {
            return assets[moduleName][resourceName][version];
        }
        return null;
    }

    /**
     * Create environment object from the manifest.
     *
     * @param manifest the manifest object.
     * @param locale the locale string such as 'en'.
     */
    public static createEnvironment(manifest: any, locale: string): any {
        // use local origin automatically.
        manifest.origin = MsftSme.getSafeOrigin();

        // initialize tracking of known trusted origins
        this.extensionOrigins = new Set<string>();
        this.extensionOrigins.add(manifest.origin);

        // use current time if version is missing so this enforce browser caching if v= option is used.
        manifest.version = manifest.version ? manifest.version : Date.now().toString();

        // merge manifest configuration with default configuration
        manifest.configuration = MsftSme.deepAssign({}, defaultEnvironmentConfiguration, manifest.configuration);

        const modules = manifest.modules || [];

        // process manifest entry points separately from children modules
        if (manifest.name === 'msft.sme.shell') {
            const shellExtensionManifest = { ...manifest };
            delete shellExtensionManifest.modules;
            shellExtensionManifest.name = 'msft.sme.shell-extensions';
            shellExtensionManifest.target = '/extensions';
            modules.push(shellExtensionManifest);
        }
        // Initialize Module Assets
        MsftSme.self().Resources.moduleAssets = {};

        // process Modules
        if (modules && modules.length > 0) {

            // first process resources, signature, and origin
            for (const module of modules) {
                // TODO: signature is going to be removed from modules, for now always use default.
                module.signature = EnvironmentModule.defaultSignature;

                // if the module has no origin, use the global one.
                module.origin = module.origin || manifest.origin;
                this.extensionOrigins.add(module.origin);

                // process module parts
                EnvironmentModule.processModuleResources(module, locale);
                delete module['resources'];

                // add assets to module assets using absolute paths
                if (module.assets) {
                    const moduleAssets: MsftSme.MsftSmeModuleAssets = {};
                    const assetKeys = Object.keys(module.assets);
                    assetKeys.forEach(key => {
                        moduleAssets[key] = {};
                        const versionKeys = Object.keys(module.assets[key]);
                        versionKeys.forEach(version => {
                            moduleAssets[key][version] = this.toAbsolutePath(module, module.assets[key][version]);
                        });
                    });

                    MsftSme.self().Resources.moduleAssets[module.name] = moduleAssets;
                }
            }

            // now process extension targets
            for (const module of modules) {
                EnvironmentModule.processExtensionTargets(module);
            }

            // finally process entry points
            for (const module of modules) {
                EnvironmentModule.processEntryPoints(module);
            }

            // default entry point collections to be sorted alphabetically
            const alphaSort = (a, b) => a.displayName ? a.displayName.localeCompareIgnoreCase(b.displayName) : -1;
            if (this.allEntryPoints) {
                this.allEntryPoints.sort(alphaSort);
            } else {
                this.allEntryPoints = [];
            }

            if (this.entryPointsByType) {
                this.entryPointsByType[EnvironmentModuleEntryPointType.Solution].sort(alphaSort);
                this.entryPointsByType[EnvironmentModuleEntryPointType.Tool].sort(alphaSort);
            }

            // TODO: do we want settings alphabetized?
        }
        return manifest;
    }

    /**
     * Gets the environment module.
     *
     * @param name the name of module.
     * @return EnvironmentModule the environment module.
     */
    public static checkEnvironmentInitialized(): void {
        if (!MsftSme || !MsftSme.self().Environment) {
            const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.EnvironmentNotInitialized.message;
            throw Error(message);
        }
    }

    /**
     * Gets the environment module.
     *
     * @param name the name of module.
     * @return EnvironmentModule the environment module.
     */
    public static getEnvironmentModule(name: string): EnvironmentModule {
        const shellEnvironment = MsftSme.self().Environment;
        const modules = shellEnvironment && shellEnvironment.modules;
        if (modules) {
            for (const module of modules) {
                if (module.name === name) {
                    return <EnvironmentModule>module;
                }
            }
        }

        if (shellEnvironment.name === name) {
            return <EnvironmentModule>shellEnvironment;
        }

        return null;
    }

    /**
     * Process extension targets for quick and easy access later
     * @param module the modules to process
     */
    private static processExtensionTargets(module: EnvironmentModule) {
        // initialize tracking structures if they have not been
        if (!EnvironmentModule.allExtensionTargets) {
            EnvironmentModule.allExtensionTargets = [];
            EnvironmentModule.extensionTargetsByType = {};
            EnvironmentModule.extensionTargetsById = {};
            EnvironmentModule.extensionTargetFulfillmentMap = {};
            Object.keys(ExtensionTargetType).forEach(key => this.extensionTargetsByType[ExtensionTargetType[key]] = []);
        }

        // stop if there are no extension targets in this module
        if (!module.extensionTargets) {
            return;
        }

        module.extensionTargets.forEach(extensionTarget => {
            // ignore invalid types in the manifest
            if (!EnvironmentModule.extensionTargetsByType[extensionTarget.extensionTargetType]) {
                Logging.log(<LogRecord>{
                    source: EnvironmentModule.logSourceName,
                    level: LogLevel.Warning,
                    consoleGroupHeader: `Unable to determine extensionTargetType.`,
                    message: `Entry point "${extensionTarget.name}" in module "${module.displayName} has a extensionTargetType of: ${extensionTarget.extensionTargetType}`
                });
                return;
            }
            // save the module information into the extension target
            extensionTarget.parentModule = module;

            const extensionTargetId = EnvironmentModule.createFormattedExtensionTarget(extensionTarget);

            // save the module information into the extension target
            extensionTarget.parentModule = module;

            // push to data structures
            EnvironmentModule.allExtensionTargets.push(extensionTarget);
            EnvironmentModule.extensionTargetsByType[extensionTarget.extensionTargetType].push(extensionTarget);
            EnvironmentModule.extensionTargetsById[extensionTargetId] = extensionTarget;

            // initialize fulfillment map
            EnvironmentModule.extensionTargetFulfillmentMap[extensionTargetId] = [];
        });
    }

    /**
     * Process Entry points for quick and easy access later
     * @param module the modules to process
     */
    private static processEntryPoints(module: EnvironmentModule) {
        // initialize tracking structures if they have not been
        if (!EnvironmentModule.allEntryPoints) {
            EnvironmentModule.allEntryPoints = [];
            EnvironmentModule.entryPointsByType = {};
            EnvironmentModule.entryPointsById = {};
            Object.keys(EnvironmentModuleEntryPointType).forEach(key => this.entryPointsByType[EnvironmentModuleEntryPointType[key]] = []);
            this.friendlyUrlMap = {
                connectionTypes: { to: {}, from: {} },
                solutions: { to: {}, from: {} },
                tools: { to: {}, from: {} },
                settingsForms: { to: {}, from: {} }
            };
        }

        if (!module.entryPoints) {
            return;
        }

        module.entryPoints.forEach(entryPoint => {
            // ignore invalid types in the manifest
            if (!EnvironmentModule.entryPointsByType[entryPoint.entryPointType]) {
                Logging.log(<LogRecord>{
                    source: EnvironmentModule.logSourceName,
                    level: LogLevel.Warning,
                    consoleGroupHeader: `Unable to determine entryPointType.`,
                    message: `Entry point "${entryPoint.displayName}" in module "${module.displayName} has a entryPointType of: ${entryPoint.entryPointType}`
                });
                return;
            }

            // if we have experiment keys, dont process the entry point if any key is not enabled
            if (entryPoint.experimentKeys && entryPoint.experimentKeys.some(key => !MsftSme.isExperimentEnabledRaw(key))) {
                return;
            }

            // save the module information into the entry point
            entryPoint.parentModule = module;

            const entryPointId = EnvironmentModule.createFormattedEntrypoint(entryPoint);

            // push to data structures
            EnvironmentModule.allEntryPoints.push(entryPoint);
            EnvironmentModule.entryPointsByType[entryPoint.entryPointType].push(entryPoint);
            EnvironmentModule.entryPointsById[entryPointId] = entryPoint;

            // process url mappings
            if (entryPoint.urlName) {

                switch (entryPoint.entryPointType) {
                    case EnvironmentModuleEntryPointType.Solution:
                        this.friendlyUrlMap.solutions.to[entryPointId] = entryPoint.urlName;
                        this.friendlyUrlMap.solutions.from[entryPoint.urlName] = entryPointId;
                        break;
                    case EnvironmentModuleEntryPointType.Tool:
                        this.friendlyUrlMap.tools.to[entryPointId] = entryPoint.urlName;
                        this.friendlyUrlMap.tools.from[entryPoint.urlName] = entryPointId;
                        break;
                    case EnvironmentModuleEntryPointType.SettingsForm:
                        this.friendlyUrlMap.settingsForms.to[entryPointId] = entryPoint.urlName;
                        this.friendlyUrlMap.settingsForms.from[entryPoint.urlName] = entryPointId;
                        break;
                    case EnvironmentModuleEntryPointType.ConnectionProvider:
                        if (entryPoint.connectionTypeUrlName) {
                            this.friendlyUrlMap.connectionTypes.to[entryPoint.connectionType] = entryPoint.connectionTypeUrlName;
                            this.friendlyUrlMap.connectionTypes.from[entryPoint.connectionTypeUrlName] = entryPoint.connectionType;
                        }
                }
            }

            if (entryPoint.entryPointType === EnvironmentModuleEntryPointType.ConnectionProvider && entryPoint.connectionTypeUrlName) {
                this.friendlyUrlMap.connectionTypes.to[entryPoint.connectionType] = entryPoint.connectionTypeUrlName;
                this.friendlyUrlMap.connectionTypes.from[entryPoint.connectionTypeUrlName] = entryPoint.connectionType;
            }

            // process extension mappings
            if (entryPoint.extends) {
                entryPoint.extends.forEach(extender => {
                    const extensionTarget = EnvironmentModule.getExtensionTargets(target => {
                        return target.parentModule.name === extender.module
                            && target.name === extender.target;
                    }).first();

                    if (MsftSme.isNullOrUndefined(extensionTarget)) {
                        // if we get here, either that target is no longer supported or the target extension is not installed.
                        return;
                    }

                    const extensionTargetId = EnvironmentModule.createFormattedExtensionTarget(extensionTarget);
                    EnvironmentModule.extensionTargetFulfillmentMap[extensionTargetId].push(entryPointId);
                });
            }
        });
    }

    /**
     * Evaluates all of the modules in the environment and returns a flat list of all of their entry points.
     * optionally filtered by the 'filter' function
     *
     * @param filter the filter to apply to the entry points.
     * @return a flat list of all module entry points
     */
    private static getExtensionTargets(filter?: (extensionTarget: ExtensionTarget) => boolean): ExtensionTarget[] {
        return filter ? EnvironmentModule.allExtensionTargets.filter(filter) : EnvironmentModule.allExtensionTargets;
    }

    /**
     * Evaluates all of the modules in the environment and returns a flat list of all of their entry points.
     * optionally filtered by the 'filter' function
     *
     * @param filter the filter to apply to the entry points.
     * @return a flat list of all module entry points
     */
    public static getEntryPoints(filter?: (entryPoint: EnvironmentModuleEntryPoint) => boolean): EnvironmentModuleEntryPoint[] {
        EnvironmentModule.checkEnvironmentInitialized();
        return filter ? EnvironmentModule.allEntryPoints.filter(filter) : EnvironmentModule.allEntryPoints;
    }

    /**
     * Gets the available entry points from all of the modules in the environment, filtered by type.
     *
     * @param name the name of module.
     * @return EnvironmentModule the environment module.
     */
    public static getEntryPointsByType(entryPointTypes: EnvironmentModuleEntryPointType[]): EnvironmentModuleEntryPoint[] {
        EnvironmentModule.checkEnvironmentInitialized();
        return entryPointTypes.mapMany<EnvironmentModuleEntryPoint>(type => EnvironmentModule.entryPointsByType[type]);
    }

    /**
     * Gets the connection type mapping data.
     *
     * @return the mapping object.
     */
    public static fulfillExtensionTarget(extensionTargetId): string[] {
        EnvironmentModule.checkEnvironmentInitialized();
        return EnvironmentModule.extensionTargetFulfillmentMap[extensionTargetId];
    }

    /**
     * Gets the connection type mapping data.
     *
     * @return the mapping object.
     */
    public static getConnectionMap(): { [name: string]: EnvironmentConnectionTypeInfo } {
        if (!EnvironmentModule.connectionMap) {
            EnvironmentModule.connectionMap = EnvironmentModule.createConnectionMap();
        }

        return EnvironmentModule.connectionMap;
    }

    /**
     * Gets a friendly url segment from a connection type
     * @param connectionType the connection type to map.
     * @return string friendly url for the connection type or the connection type if no friendly name exists.
     */
    public static getFriendlyUrlSegmentForConnectionType(connectionType: string, fallbackToUnfriendlySegment = true): string {
        return EnvironmentModule.friendlyUrlMap.connectionTypes.to[connectionType] || (fallbackToUnfriendlySegment ? connectionType : null);
    }

    /**
     * Gets a connection type from a friendly url segment
     * @param urlSegment the url segment to map
     * @return string connection type found using the friendly url segment
     */
    public static getConnectionTypeFromFriendlyUrlSegment(urlSegment: string): string {
        return EnvironmentModule.friendlyUrlMap.connectionTypes.from[urlSegment];
    }

    /**
     * Gets a friendly url segment from an entry point id
     * @param urlSegment the url segment.
     * @param entryPointType the type of entry point to look for.
     * @return string friendly url for the entry point id
     */
    public static getFriendlyUrlSegmentForEntryPoint(
        entryPoint: string, entryPointType: EnvironmentModuleEntryPointType, fallbackToUnfriendlySegment = true): string {
        switch (entryPointType) {
            case EnvironmentModuleEntryPointType.Solution:
                return EnvironmentModule.friendlyUrlMap.solutions.to[entryPoint] || (fallbackToUnfriendlySegment ? entryPoint : null);
            case EnvironmentModuleEntryPointType.Tool:
                return EnvironmentModule.friendlyUrlMap.tools.to[entryPoint] || (fallbackToUnfriendlySegment ? entryPoint : null);
            case EnvironmentModuleEntryPointType.SettingsForm:
                return EnvironmentModule.friendlyUrlMap.settingsForms.to[entryPoint] || (fallbackToUnfriendlySegment ? entryPoint : null);
        }

        return null;
    }

    /**
     * Gets a friendly url segment for an entry point
     * @param urlSegment the url segment.
     * @param entryPointType the type of entry point to look for.
     * @return string friendly url for the entry point
     */
    public static getEntryPointFromFriendlyUrlSegment(urlSegment: string, entryPointType: EnvironmentModuleEntryPointType): string {
        switch (entryPointType) {
            case EnvironmentModuleEntryPointType.Solution:
                return EnvironmentModule.friendlyUrlMap.solutions.from[urlSegment];
            case EnvironmentModuleEntryPointType.Tool:
                return EnvironmentModule.friendlyUrlMap.tools.from[urlSegment];
            case EnvironmentModuleEntryPointType.SettingsForm:
                return EnvironmentModule.friendlyUrlMap.settingsForms.from[urlSegment];
        }

        return null;
    }

    /**
     * Gets the connection type information.
     *
     * @param typeName the type name.
     * @return EnvironmentConnectionTypeInfo the connection type information.
     */
    public static getConnectionTypeInfo(typeName: string): EnvironmentConnectionTypeInfo {
        return EnvironmentModule.getConnectionMap()[typeName];
    }

    private static createConnectionMap(): { [name: string]: EnvironmentConnectionTypeInfo } {
        const map: { [name: string]: EnvironmentConnectionTypeInfo } = {};
        const providers = this.getEntryPointsByType([EnvironmentModuleEntryPointType.ConnectionProvider]);
        providers.forEach(provider => {
            if (!provider.connectionTypeDefaultSolution) {
                const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.EnvironmentMissingDefault.message;
                throw new Error(message);
            }

            const solutionEntrypoint = EnvironmentModule.splitFormattedEntrypoint(provider.connectionTypeDefaultSolution);
            const solution = this.getEntryPointsByType([EnvironmentModuleEntryPointType.Solution])
                .first(ep => ep.parentModule.name === solutionEntrypoint.moduleName && ep.name === solutionEntrypoint.entrypointName);
            map[provider.connectionType] = <EnvironmentConnectionTypeInfo>{ provider, solution };
            if (solution && solution.parentModule && solution.parentModule.name) {
                map[solution.parentModule.name] = map[provider.connectionType];
            }
        });

        return map;
    }

    /**
     * splits an entrypoint identifier string into its respective module and entrypoint names
     * @param format the formatted entrypoint identifier string
     */
    public static splitFormattedEntrypoint(format: string) {
        const parts = format.split('!');
        return { moduleName: parts[0], entrypointName: parts[1] };
    }

    /**
     * creates a formatted entrypoint identifier string from an entrypoint
     * @param entryPoint the entrypoint to create the string from
     */
    public static createFormattedEntrypoint(entryPoint: EnvironmentModuleEntryPoint) {
        return EnvironmentModule.createEntrypointId(entryPoint.parentModule.name, entryPoint.name);
    }

    public static createEntrypointId(moduleName: string, entryPointName: string) {
        return `${moduleName}!${entryPointName}`;
    }

    /**
     * Create a formatted iframe ID used to link iframes and their html element
     * Replace . with - because . is not permitted in css selectors required for testing
     * @param parentModuleName the name of the parent module of the iframe content
     * @param entryPointName the name of the entry point loaded by the iframe
     */
    public static createFormattedIFrameId(parentModuleName: string, entryPointName: string): string {
        const parentModuleId = MsftSme.replaceAll(parentModuleName, '.', '-');
        return '{0}-{1}'.format(parentModuleId, entryPointName);
    }

    /**
     * splits an extension target identifier string into its respective module and extension target names
     * @param format the formatted extension target identifier string
     */
    public static splitFormattedExtensionTarget(format: string) {
        const parts = format.split('!');
        return { moduleName: parts[0], extensionTargetName: parts[1] };
    }

    /**
     * creates a formatted extension target identifier string from an extension target
     * @param entryPoint the extension target to create the string from
     */
    public static createFormattedExtensionTarget(extensionTarget: ExtensionTarget) {
        return `${extensionTarget.parentModule.name}!${extensionTarget.name}`;
    }

    /**
     * resolves an entrypoint from a formatted entrypoint identifier string
     * @param formattedEntrypointIdentifier the formatted entrypoint identifier string
     */
    public static resolveEntrypoint(formattedEntrypointIdentifier: string): EnvironmentModuleEntryPoint {
        if (!formattedEntrypointIdentifier) {
            return null;
        }
        const parts = EnvironmentModule.splitFormattedEntrypoint(formattedEntrypointIdentifier);
        if (!parts.moduleName || !parts.entrypointName) {
            return null;
        }
        const entryPoints = this.getEntryPoints(ep => ep.name === parts.entrypointName && ep.parentModule.name === parts.moduleName);
        return entryPoints[0] || null;
    }

    /**
     * Gets the name of current shell or module.
     */
    public static getModuleName(): string {
        const self = MsftSme.self();
        return self.Init.moduleName;
    }

    /**
     * Gets the version of current shell or module.
     */
    public static getModuleVersion(): string {
        const self = MsftSme.self();
        if (self.Environment.version) {
            return self.Environment.version;
        }

        for (const module of self.Environment.modules) {
            if (module.name === self.Init.moduleName && module.version) {
                self.Environment.version = module.version;
                return module.version;
            }
        }

        return '0.0.0';
    }

    /**
     * Compare the shell version with specified version.
     * - this function must be called after NavigationService Init was called, otherwise it
     * cannot get current shell version and return false.
     * - Tag format is expected be like "<Major>.<Minor>.<Patch>-<Tag>.<Tag version>".
     *   ex) 1.2.3-myTagName.123
     *
     * @param inputVersion the version to compare.
     * @return true if shell has newer version running.
     */
    public static shellGreaterOrEqual(inputVersion: string): boolean {
        if (MsftSme.isNullOrWhiteSpace(inputVersion)) {
            // shell doesn't give version.
            Logging.logError('EnvironmentModule.shellGreaterOrEqual', 'version is empty.');
            return false;
        }

        const shellVersion = MsftSme.self().Init.shellVersion;
        if (MsftSme.isNullOrWhiteSpace(shellVersion)) {
            // shell doesn't give version.
            Logging.logError('EnvironmentModule.shellGreaterOrEqual', 'shell version is not available.');
            return false;
        }

        const tagSegments = inputVersion.split("-");
        let inputCoreVersion = inputVersion;
        let inputTag = "";
        let inputTagVersion = 0;
        if (tagSegments.length === 2) {
            inputCoreVersion = tagSegments[0];
            const tag = tagSegments[1].split(".");
            inputTag = tag[0];
            if (tag.length === 2) {
                const tagNumber = Number(tag[1]);
                inputTagVersion = isNaN(tagNumber) ? 0: tagNumber;
            }
        }

        const inputSegments = inputCoreVersion.split('.');
        if (inputSegments.length !== 3) {
            // shell doesn't give right version format.
            Logging.logError('EnvironmentModule.shellGreaterOrEqual', 'version format is not right.');
            return false;
        }

        const shellTagSegments = shellVersion.split("-");
        let shellCoreVersion = shellVersion;
        let shellTag = "";
        let shellTagVersion = 0;
        if (shellTagSegments.length === 2) {
            shellCoreVersion = shellTagSegments[0];
            const tag = shellTagSegments[1].split(".");
            shellTag = tag[0];
            if (tag.length === 2) {
                const tagNumber = Number(tag[1]);
                shellTagVersion = isNaN(tagNumber) ? 0: tagNumber;
            }
        }

        const shellSegments = shellCoreVersion.split('.');
        if (shellSegments.length !== 3) {
            // shell doesn't give right version format.
            Logging.logError('EnvironmentModule.shellGreaterOrEqual', 'shell version is corrupted.');
            return false;
        }

        const inputDigits = inputSegments.map(digit => {
            const num = Number(digit);
            return isNaN(num) ? 0 : num;
        });
        const shellDigits = shellSegments.map(digit => {
            const num = Number(digit);
            return isNaN(num) ? 0 : num;
        });
        if (shellDigits[0] < inputDigits[0]
            || (shellDigits[0] === inputDigits[0] && shellDigits[1] < inputDigits[1])
            || (shellDigits[0] === inputDigits[0] && shellDigits[1] === inputDigits[1] && shellDigits[2] < inputDigits[2])
            || (shellDigits[0] === inputDigits[0] && shellDigits[1] === inputDigits[1] && shellDigits[2] === inputDigits[2]
                && shellTag !== "" && shellTag.localeCompare(inputTag) < 0)
            || (shellDigits[0] === inputDigits[0] && shellDigits[1] === inputDigits[1] && shellDigits[2] === inputDigits[2]
                && shellTag !== "" && shellTag.localeCompare(inputTag) === 0) && shellTagVersion < inputTagVersion) {
            return false;
        }

        return true;
    }

    /**
     * Check if the gateway uses API set of version 2.0.0.
     * @returns true if version 2.0.0
     */
    public static get isGatewayV200(): boolean {
        return MsftSme.self().Init.gatewayApiVersion === '2.0.0';
    }

    /**
     * check if the gateway runs on Windows platform.
     * @returns true if Windows platform.
     */
    public static get isGatewayWindows(): boolean {
        return MsftSme.self().Init.gatewayPlatform === 'Windows';
    }

    /**
     * Check if the gateway runs on Linux platform.
     * @returns true if Linux platform.
     */
    public static get isGatewayLinux(): boolean {
        return MsftSme.self().Init.gatewayPlatform === 'Linux';
    }
}
